import React, { useMemo } from "react"

import { TwitterTweetEmbed } from "react-twitter-embed"

import { documentToPlainTextString } from "@contentful/rich-text-plain-text-renderer"
import {
  Options,
  documentToReactComponents,
} from "@contentful/rich-text-react-renderer"
import { BLOCKS, INLINES, Text } from "@contentful/rich-text-types"
import SubTitle from "decentraland-gatsby/dist/components/Text/SubTitle"
import Title from "decentraland-gatsby/dist/components/Text/Title"
import useFormatMessage from "decentraland-gatsby/dist/hooks/useFormatMessage"
import { Link } from "decentraland-gatsby/dist/plugins/intl"
import Time from "decentraland-gatsby/dist/utils/date/Time"
import { Container } from "decentraland-ui/dist/components/Container/Container"
import unescape from "lodash/unescape"

import {
  isWebpSupported,
  optimize,
  useImageOptimization,
} from "../../hooks/contentful"
import { BodyImageReference } from "../../hooks/useBlogContentfulQuery"
import { BlogPostAttributes } from "../../modules/formats"
import locations from "../../modules/locations"
import { slugify } from "../../modules/string"
import Image from "../Contentful/Image"
import { Facebook } from "../Icon/Facebook"
import { Twitter } from "../Icon/Twitter"
import LoadingText from "../Loading/LoadingText"

import "./Post.css"

export const richTextOptions = (
  referencesMap: Record<string, any>
): Options => {
  return {
    renderNode: {
      [BLOCKS.EMBEDDED_ASSET]: (node) => {
        const reference = referencesMap[node.data.target.sys.id]
        if (!reference && !node.data?.target?.fields?.file?.url) {
          return null
        }

        const image = {
          url: reference?.url || node.data.target.fields.file.url,
          width:
            reference?.width ||
            node.data.target.fields.file.details.image.width,
          height:
            reference?.height ||
            node.data.target.fields.file.details.image.height,
        }
        const optimizedImage = optimize(image.url)
        return (
          <Image
            className="post-content__image"
            format={isWebpSupported() ? `webp` : `jpg`}
            src={optimizedImage.optimized}
            width={image.width}
            height={image.height}
          />
        )
      },
      [BLOCKS.QUOTE]: (node, children: React.ReactNode) => (
        <blockquote className="twitter-tweet">{children}</blockquote>
      ),
      [BLOCKS.HEADING_2]: (node, children: React.ReactNode) => {
        const contentString = documentToPlainTextString(node)
        return (
          <div className="post-content__heading-container">
            <Title id={slugify(contentString)}>
              <Link
                href={`#${slugify(contentString)}`}
                className="post__content-title-anchor"
              >
                #
              </Link>
              {children}
            </Title>
          </div>
        )
      },
      [BLOCKS.HEADING_3]: (node, children: React.ReactNode) => {
        const contentString = documentToPlainTextString(node)
        return (
          <div className="post-content__heading-container">
            <SubTitle id={slugify(contentString)}>
              <Link
                href={`#${slugify(contentString)}`}
                className="post__content-title-anchor"
              >
                #
              </Link>
              {children}
            </SubTitle>
          </div>
        )
      },
      [BLOCKS.HEADING_4]: (node, children: React.ReactNode) => {
        const contentString = documentToPlainTextString(node)
        return (
          <div className="post-content__heading-container">
            <SubTitle className="heading-4" id={slugify(contentString)}>
              <Link
                href={`#${slugify(contentString)}`}
                className="post__content-title-anchor"
              >
                #
              </Link>
              {children}
            </SubTitle>
          </div>
        )
      },
      [BLOCKS.HEADING_5]: (node, children: React.ReactNode) => {
        const contentString = documentToPlainTextString(node)
        return (
          <div className="post-content__heading-container">
            <SubTitle className="heading-5" id={slugify(contentString)}>
              <Link
                href={`#${slugify(contentString)}`}
                className="post__content-title-anchor"
              >
                #
              </Link>
              {children}
            </SubTitle>
          </div>
        )
      },
      [INLINES.HYPERLINK]: (node) => {
        const content = node.content as [Text]
        if (
          (node.data.uri.includes("youtube.com") ||
            node.data.uri.includes("youtu.be")) &&
          content[0].value === node.data.uri
        ) {
          const url = new URL(node.data.uri)

          const videoCode = url.searchParams.has("v")
            ? url.searchParams.get("v")
            : url.pathname.split("/").pop()

          return (
            <iframe
              width="560"
              height="315"
              src={`https://www.youtube.com/embed/${videoCode}`}
              title="YouTube video player"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          )
        } else if (
          node.data.uri.includes("twitter.com") &&
          content[0].value === node.data.uri
        ) {
          const twitterSplit = node.data.uri.split("/")
          const twitteId = twitterSplit[twitterSplit.length - 1].split("?")[0]
          return (
            <div className="twitter-container">
              <TwitterTweetEmbed
                tweetId={twitteId}
                options={{ theme: "dark" }}
              />
            </div>
          )
        } else if (
          node.data.uri.includes("linkedin.com") &&
          content[0].value === node.data.uri
        ) {
          return (
            <iframe
              src={`${node.data.uri}`}
              height="760"
              width="560"
              frameBorder="0"
              title="Embedded Linkedin Post"
              style={{ border: "thin solid lightgray", padding: "5px" }}
            ></iframe>
          )
        } else {
          const target =
            node.data.uri.startsWith("#") ||
            (node.data.uri.startsWith("https://decentraland.org/blog") &&
              node.data.uri.includes("#"))
              ? "_self"
              : "_blank"
          return (
            <a href={node.data.uri} target={target}>
              {content[0].value}
            </a>
          )
        }
      },
    },
    renderText: (text) => unescape(text),
  }
}

export type PostProps = {
  post: BlogPostAttributes | null
  loading: boolean
}

export default function Post(props: PostProps) {
  const { post, loading } = props
  const l = useFormatMessage()
  const image = useImageOptimization(post?.image)

  const referencesMap = useMemo(() => {
    if (!post) {
      return {}
    }
    return (
      post.bodyReferences?.reduce<Record<string, BodyImageReference>>(
        (referencesObj, reference) => {
          referencesObj[reference.contentful_id] = reference
          return referencesObj
        },
        {}
      ) || {}
    )
  }, [post])

  const authorImage = useImageOptimization(post?.authorImage)

  return (
    <Container className="blog__container">
      <div
        className="post__image"
        style={
          !loading && image.jpg
            ? {
                backgroundImage: `url(${
                  (isWebpSupported() && image.webp) || image.jpg
                })`,
              }
            : {}
        }
      ></div>
      {loading && (
        <>
          <div className="post__loading-header">
            <LoadingText type="span" size="small" />
            <LoadingText type="span" size="small" />
          </div>
          <LoadingText type="h1" size="large" lines={2} />
          <LoadingText type="h2" size="full" lines={4} />
          <div className="post__loading-social">
            <LoadingText type="span" size="medium" />
            <LoadingText type="span" size="medium" />
          </div>
          <div className="post__loading-body">
            <LoadingText type="p" size="full" lines={20} />
          </div>
        </>
      )}
      {!loading && post && (
        <div className="post__info">
          <span className="post__date">
            {post.publishedDate &&
              Time.utc(post.publishedDate).format("MMM DD, YYYY")}
          </span>

          <Link
            className="post__category"
            href={locations.category(post.categoryId!)}
          >
            {post.categoryTitle}
          </Link>

          <Title>{post.title && unescape(post.title)}</Title>
          <SubTitle>{post.description && unescape(post.description)}</SubTitle>
        </div>
      )}
      {!loading && post?.authorId && (
        <div className="post__author">
          <div className="author__info">
            <Link href={locations.author(post.authorId)}>
              <img
                src={isWebpSupported() ? authorImage.webp : authorImage.jpg}
                alt={post.authorTitle}
                className="img-fluid"
              />
              <span>{post.authorTitle}</span>
            </Link>
          </div>
          <div className="author__social">
            <span>{l("page.blog.share")}</span>
            <Link href={locations.twitter(post)}>
              <Twitter />
            </Link>
            <Link href={locations.facebook(post)}>
              <Facebook />
            </Link>
          </div>
        </div>
      )}
      {!loading && post?.body && (
        <div className="post__content">
          {documentToReactComponents(post.body, richTextOptions(referencesMap))}
        </div>
      )}
    </Container>
  )
}
